import {
  type Command,
  type MethodBase,
  type ApiFailure,
  Client,
  isGatewayForbidden,
  isUnsignedRequest,
  toApiFailure,
} from '@perfectpost/perfect-post-common';
import * as Sentry from '@sentry/react';
import {fetchAuthSession} from 'aws-amplify/auth';
import {ConsoleLogger} from 'aws-amplify/utils';

import {notifySessionExpired} from './sessionExpiry';

const logger = new ConsoleLogger('SessionAwareClient');

/**
 * Le `Client` de l'API, qui sait distinguer « pas de session » de « session expirée ».
 *
 * Les routes de l'API sont en `authorizer: aws_iam` : chaque requête est signée avec les
 * identifiants temporaires du pool d'identités Cognito, qu'Amplify renouvelle seul (1 h).
 * Quand la signature ne passe pas, API Gateway répond un 403 SANS code PerfectPost (« The
 * security token included in the request is invalid. », « Signature expired », « Missing
 * Authentication Token ») — que l'interface affichait comme « Votre compte ne permet pas
 * cette action », alors que le compte n'y est pour rien. L'en-tête `x-amzn-errortype` de ce
 * 403 dit lequel des trois cas on tient :
 *
 * 1. **Requête partie NON signée** (`MissingAuthenticationTokenException`,
 *    `isUnsignedRequest`) : Amplify n'avait aucun identifiant — pas d'utilisateur Cognito
 *    sur cette page, et le pool d'identités refuse les invités. Il n'y a rien à renouveler
 *    ni à rejouer, et rien à signaler : l'`ApiFailure` `unauthorized` remonte telle quelle,
 *    comme avant. C'était le cas de loin le plus fréquent (PERFECTPOST-WEB-10X : l'iframe
 *    `/chat` de l'extension), et il était pris pour une expiration.
 * 2. **Signature périmée, rejouée avec succès** : on force le renouvellement des
 *    identifiants (`fetchAuthSession({forceRefresh: true})`) et on rejoue la requête UNE
 *    fois. Un avertissement Sentry, une fois par chargement de page.
 * 3. **Encore refusée après renouvellement** : la session est bel et bien finie. On
 *    prévient l'interface (`notifySessionExpired`) et on laisse remonter l'`ApiFailure`
 *    `unauthorized`.
 *
 * Aucune autre erreur n'est rejouée — republier un post sur un timeout ferait un doublon.
 *
 * `trySend` hérite de ce comportement puisqu'il passe par `send`.
 */
export class SessionAwareClient extends Client {
  override async send<M extends MethodBase, T>(command: Command<M, T>): Promise<T> {
    try {
      return await super.send(command);
    } catch (error: unknown) {
      const failure = await toApiFailure(error);
      if (!isGatewayForbidden(failure)) {
        // Comportement historique : l'erreur Amplify remonte telle quelle.
        throw error;
      }
      if (isUnsignedRequest(failure)) {
        // Pas de session du tout, rien à renouveler : l'appelant voit un `unauthorized`.
        throw failure;
      }
      reportGatewayForbidden(failure, command);
      await renewCredentials();
      try {
        return await super.send(command);
      } catch (retryError: unknown) {
        const retryFailure = await toApiFailure(retryError);
        if (isGatewayForbidden(retryFailure)) {
          notifySessionExpired();
        }
        throw retryFailure;
      }
    }
  }
}

/**
 * Force Amplify à redemander jeton et identifiants IAM. Un échec est avalé : si le jeton de
 * rafraîchissement est mort, Amplify a vidé ses jetons et la seconde tentative partira non
 * signée — la passerelle la refusera, et c'est ELLE qui tranche.
 */
async function renewCredentials(): Promise<void> {
  try {
    await fetchAuthSession({forceRefresh: true});
  } catch (error: unknown) {
    logger.warn('Le renouvellement des identifiants a échoué, la requête est rejouée telle quelle.', error);
  }
}

let gatewayForbiddenReported = false;

/**
 * Trace Sentry, en avertissement, UNE fois par chargement de page : ce que la passerelle
 * renvoie exactement dans ces cas, on ne l'a jamais vu — ni dans les journaux des Lambdas
 * (la requête ne les atteint pas), ni dans ceux d'API Gateway (pas de journal d'accès).
 * `gatewayErrorType` (`x-amzn-errortype`) dit lequel des refus de la passerelle on tient ;
 * `gatewayMessage` est masqué par le nettoyage de données de Sentry, réglage du projet.
 */
function reportGatewayForbidden(failure: ApiFailure, command: Command<MethodBase, unknown>): void {
  if (gatewayForbiddenReported) {
    return;
  }
  gatewayForbiddenReported = true;
  Sentry.captureMessage('API Gateway : 403 sans code PerfectPost, identifiants renouvelés puis requête rejouée', {
    level: 'warning',
    extra: {
      statusCode: failure.statusCode,
      path: command.input.path,
      gatewayErrorType: failure.gatewayErrorType,
      gatewayMessage: failure.message,
    },
  });
}
